import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  sumMoney
} from '@/components/WorkbookPage';
import { Snowflake, TrendingDown } from 'lucide-react';

export const Rule8Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule8 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule8', { ...data, [field]: value });
  };
  
  const [debts, setDebts] = useState(data.debts || [
    { name: '', balance: '', minPayment: '', apr: '' },
    { name: '', balance: '', minPayment: '', apr: '' },
    { name: '', balance: '', minPayment: '', apr: '' },
    { name: '', balance: '', minPayment: '', apr: '' },
    { name: '', balance: '', minPayment: '', apr: '' },
  ]);
  
  const updateDebt = (index, field, value) => {
    const newDebts = [...debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    setDebts(newDebts);
    updateFormData('rule8', {
      ...data,
      debts: newDebts,
      totalMinPayments: sumMoney(newDebts, 'minPayment').toFixed(2)
    });
  };

  const totalMinPayments = sumMoney(debts, 'minPayment');
  
  return (
    <WorkbookPage pageId="rule-8" prevPage="rule-7" nextPage="section-3-divider">
      <RuleHeader ruleNumber="8" title="The Debt Snowball Method" section="control" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        The debt snowball method is a strategy for paying off multiple debts. You list all 
        your debts from smallest balance to largest, pay minimums on everything, and throw 
        all extra money at the smallest debt. Once it's gone, you "roll" that payment to 
        the next smallest. Like a snowball rolling downhill, your payment power grows.
      </Paragraph>
      
      <SectionTitle>Understanding Interest (APR)</SectionTitle>
      <Paragraph>
        <strong>APR (Annual Percentage Rate)</strong> is how much it costs to borrow money, 
        expressed as a yearly percentage. A credit card with 24% APR means you pay about 
        24% of your balance in interest per year if you don't pay it off.
      </Paragraph>
      
      <div className="my-6 bg-destructive/5 rounded-lg p-5">
        <h4 className="font-semibold mb-3">Why Interest Matters</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Example: You owe $1,000 at 24% APR. If you only make minimum payments ($25/month), 
          it will take over 5 years to pay off—and you'll pay about $600 in interest alone. 
          That $1,000 purchase actually cost you $1,600.
        </p>
        <p className="text-sm font-medium">
          Higher APR = more urgency to pay off
        </p>
      </div>
      
      <SectionTitle>Snowball vs. Avalanche</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="bg-primary/5 rounded-lg p-4">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <Snowflake className="h-4 w-4" />
            Snowball Method
          </h4>
          <p className="text-sm text-muted-foreground mb-2">
            Pay off <strong>smallest balance first</strong>
          </p>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>✓ Quick wins build motivation</li>
            <li>✓ Psychologically powerful</li>
            <li>✓ Best for most people</li>
          </ul>
        </div>
        <div className="bg-secondary rounded-lg p-4">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Avalanche Method
          </h4>
          <p className="text-sm text-muted-foreground mb-2">
            Pay off <strong>highest APR first</strong>
          </p>
          <ul className="text-xs space-y-1 text-muted-foreground">
            <li>✓ Saves the most money mathematically</li>
            <li>✓ Takes longer to see progress</li>
            <li>✓ Best if you're very disciplined</li>
          </ul>
        </div>
      </div>
      
      <Callout type="tip">
        <strong>Our recommendation:</strong> Use the Snowball method. The psychological wins 
        of eliminating debts keep you motivated. The "best" method is the one you'll stick with.
      </Callout>
      
      <ExampleBox title="Debt Snowball in Action">
        <p className="mb-3">Lisa has three debts:</p>
        <div className="text-sm space-y-1 mb-4">
          <p>1. Medical bill: $300 (min $25/mo)</p>
          <p>2. Credit card: $1,500 (min $50/mo, 22% APR)</p>
          <p>3. Car loan: $4,000 (min $200/mo, 7% APR)</p>
        </div>
        <p className="text-sm mb-2">
          <strong>Total minimum payments:</strong> $275/mo
        </p>
        <p className="text-sm mb-4">
          <strong>Lisa can pay:</strong> $400/mo total
        </p>
        <div className="bg-white/50 rounded p-3 text-sm">
          <p className="font-medium mb-2">Month 1-3:</p>
          <p>Pay minimums on everything, put extra $125 toward medical bill</p>
          <p className="text-success mt-2">Medical bill PAID OFF in ~3 months! ✓</p>
          <p className="font-medium mb-2 mt-4">Month 4+:</p>
          <p>Now $150/mo ($25 + $125) goes to credit card instead</p>
          <p>Credit card payoff accelerates dramatically</p>
        </div>
      </ExampleBox>
      
      <StepList steps={[
        "List all your debts with balances, minimum payments, and APRs.",
        "Order them from smallest balance to largest.",
        "Pay minimums on everything except the smallest debt.",
        "Put ALL extra money toward the smallest debt.",
        "When it's paid off, add that payment to the next debt.",
        "Repeat until debt-free.",
        "Celebrate each debt you eliminate!"
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Debt Snowball Worksheet</h3>
        
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-2">Debt Name</th>
                  <th className="text-right py-2 px-2">Balance</th>
                  <th className="text-right py-2 px-2">Min Payment</th>
                  <th className="text-right py-2 pl-2">APR %</th>
                </tr>
              </thead>
              <tbody>
                {debts.map((debt, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="py-2 pr-2">
                      <input
                        type="text"
                        className="w-full bg-transparent focus:outline-none"
                        placeholder="Debt name"
                        value={debt.name}
                        onChange={(e) => updateDebt(index, 'name', e.target.value)}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        className="w-full text-right bg-transparent focus:outline-none"
                        placeholder="$___"
                        value={debt.balance}
                        onChange={(e) => updateDebt(index, 'balance', e.target.value)}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        className="w-full text-right bg-transparent focus:outline-none"
                        placeholder="$___"
                        value={debt.minPayment}
                        onChange={(e) => updateDebt(index, 'minPayment', e.target.value)}
                      />
                    </td>
                    <td className="py-2 pl-2">
                      <input
                        type="text"
                        className="w-full text-right bg-transparent focus:outline-none"
                        placeholder="___%"
                        value={debt.apr}
                        onChange={(e) => updateDebt(index, 'apr', e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <AutoTotal
            label="Total of all minimum payments:"
            value={totalMinPayments}
            hint="Adds up automatically from the debts above"
            testId="rule8-total-min"
          />
          
          <WorksheetInput
            label="How much can you put toward debt each month?"
            placeholder="$___/month"
            value={data.monthlyDebtPayment || ''}
            onChange={(v) => updateData('monthlyDebtPayment', v)}
          />
          
          <WorksheetInput
            label="Which debt will you attack first? (smallest balance)"
            placeholder="Debt name and current balance"
            value={data.firstTarget || ''}
            onChange={(v) => updateData('firstTarget', v)}
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I've listed all my debts
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I understand APR and why it matters
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I know which debt to attack first
        </CheckboxItem>
        <CheckboxItem checked={data.checklist4 || false} onChange={(v) => updateData('checklist4', v)}>
          I have a monthly amount to put toward debt
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "List debts smallest to largest",
        "Pay minimums on all, attack smallest with extra",
        "Roll payments as debts disappear",
        "APR = cost of borrowing (higher = more urgent)",
        "The snowball builds momentum and motivation"
      ]} />
      
      <AffirmationBox>
        I am taking control of my debt, one payment at a time. Each debt I eliminate brings me closer to freedom.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="How does it feel to have a debt payoff plan?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
